import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  EnvironmentScopeContext,
  type EnvironmentScopeContextValue,
} from '@/providers/environmentScopeContext';

type EnvironmentScopeValue = 'all' | `${number}`;

const ENVIRONMENT_SCOPE_STORAGE_KEY = 'admin-portal.environment-scope';

function isEnvironmentScopeValue(value: string | null): value is EnvironmentScopeValue {
  return value === 'all' || /^[0-9]+$/.test(value ?? '');
}

export function EnvironmentScopeProvider({ children }: PropsWithChildren) {
  const [environmentScope, setEnvironmentScope] = useState<EnvironmentScopeValue>(() => {
    if (typeof window === 'undefined') {
      return 'all';
    }

    const storedScope = window.localStorage.getItem(ENVIRONMENT_SCOPE_STORAGE_KEY);
    return isEnvironmentScopeValue(storedScope) ? storedScope : 'all';
  });

  useEffect(() => {
    window.localStorage.setItem(ENVIRONMENT_SCOPE_STORAGE_KEY, environmentScope);
  }, [environmentScope]);

  const value = useMemo<EnvironmentScopeContextValue>(() => {
    const selectedEnvironmentId = environmentScope === 'all' ? undefined : Number(environmentScope);

    return {
      environmentScope,
      selectedEnvironmentId,
      setEnvironmentScope,
    };
  }, [environmentScope]);

  return <EnvironmentScopeContext.Provider value={value}>{children}</EnvironmentScopeContext.Provider>;
}